/**
 * MyWorkPanel — Sintetica "vista personale" dell'operatore loggato.
 *
 * Spec (20/02/2026, richiesta utente):
 *   "Crea un bottone sul desk principale dove il singolo operatore
 *    legge tutte le pratiche, le scadenze, le diario giornaliero,
 *    vengono letti al suo interno in modo sintetico (filtro operatore
 *    = quello di ingresso). Mantenere il filtro Studio/Agenzia
 *    già esistente."
 *
 * Implementazione:
 *   - Bottone "Il mio lavoro" nel Cruscotto, apre un Dialog
 *   - Dentro al dialog 3 sezioni sintetiche:
 *       1. Pratiche assegnate a me (status ≠ Chiusa/Archiviata)
 *       2. Scadenze dove sono assegnato (status = attiva, future
 *          o entro 90 giorni)
 *       3. Note Diario indirizzate a me (requiresAction, non completate)
 *   - Filtro: matcha sia il legacy `*Id` (single) che il nuovo `*Ids[]`
 *     (multi-assign 20/02/2026). Studio/Agenzia compatibility filter
 *     applicato a monte dal backend (lista già filtrata).
 *   - Click su una riga → naviga al tab corrispondente.
 */
import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Briefcase, CalendarClock, MessageSquare, ChevronRight,
  FolderOpen, AlertTriangle, CheckCircle2, UserCheck, Loader2,
  ClipboardList,
} from "lucide-react";
import api from "@/api";

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("it-IT"); } catch { return d; }
};

// Helper: una pratica/scadenza/nota è assegnata all'operatore corrente
// se contiene `opId` nel campo legacy `*Id` OPPURE nell'array `*Ids[]`
// (20/02/2026 multi-assign).
function isAssignedToMe(record, fieldLegacy, fieldArray, opId) {
  if (!opId) return false;
  if ((record[fieldLegacy] || "") === opId) return true;
  const arr = record[fieldArray] || [];
  return Array.isArray(arr) && arr.includes(opId);
}

const CLOSED_CASE_STATUSES = new Set([
  "Chiusa", "Conclusa",
]);

export function MyWorkPanel({
  currentOperator,
  cases = [],
  setActiveTab,
  prominent = false,
  // v1.11.0 (Fase A UX v3) — dialog controllabile dall'esterno per
  // permettere alla SuiteTopBar di aprire il pannello dalla propria
  // icona senza duplicare il trigger legacy.
  open: openProp,
  onOpenChange: onOpenChangeProp,
  hideDefaultTrigger = false,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChangeProp?.(v);
    else setInternalOpen(v);
  };
  const [deadlines, setDeadlines] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dailyPractices, setDailyPractices] = useState([]);
  const [loading, setLoading] = useState(false);

  const opId = currentOperator?.id || "";

  // Carica scadenze + diario + lavoro quotidiano solo all'apertura.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.getCustomDeadlines().catch(() => []),
      api.getContacts().catch(() => []),
      api.listDailyPractices().catch(() => []),
    ]).then(([dl, ct, dp]) => {
      if (cancelled) return;
      setDeadlines(Array.isArray(dl) ? dl : []);
      setContacts(Array.isArray(ct) ? ct : []);
      setDailyPractices(Array.isArray(dp) ? dp : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open]);

  // === Filtri "mie cose" ===
  const myCases = useMemo(() => {
    if (!opId) return [];
    return (cases || [])
      .filter((c) => !CLOSED_CASE_STATUSES.has(c.status))
      .filter((c) => isAssignedToMe(c, "assignedOperatorId", "assignedOperatorIds", opId))
      .sort((a, b) => (b.openingDate || "").localeCompare(a.openingDate || ""));
  }, [cases, opId]);

  const myDeadlines = useMemo(() => {
    if (!opId) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 90);
    const cutoffISO = cutoff.toISOString().slice(0, 10);
    return deadlines
      .filter((d) => d.status !== "completata")
      .filter((d) => (d.deadlineDate || "9999-12-31") <= cutoffISO)
      .filter((d) => isAssignedToMe(d, "operatorId", "operatorIds", opId))
      .sort((a, b) => (a.deadlineDate || "").localeCompare(b.deadlineDate || ""));
  }, [deadlines, opId]);

  const myContacts = useMemo(() => {
    if (!opId) return [];
    return contacts
      .filter((c) => !c.completedAt && c.requiresAction)
      .filter((c) => isAssignedToMe(c, "destinatarioId", "destinatarioIds", opId))
      .sort((a, b) => (b.contactDate || "").localeCompare(a.contactDate || ""));
  }, [contacts, opId]);

  // Lavoro Quotidiano assegnato a me (status != chiusa)
  const myDailyPractices = useMemo(() => {
    if (!opId) return [];
    return dailyPractices
      .filter((p) => p.status !== "chiusa")
      .filter((p) => isAssignedToMe(p, "assignedOperatorId", "assignedOperatorIds", opId))
      .sort((a, b) => (a.expectedDueDate || "").localeCompare(b.expectedDueDate || ""));
  }, [dailyPractices, opId]);

  const totalCount = myCases.length + myDeadlines.length + myContacts.length + myDailyPractices.length;

  // Urgency dot per scadenza
  const urgencyColor = (dateStr) => {
    const now = new Date();
    const target = new Date(dateStr);
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "bg-red-500";
    if (diff <= 3) return "bg-red-400";
    if (diff <= 7) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const handleNavigate = (tab) => {
    setOpen(false);
    setActiveTab?.(tab);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideDefaultTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="default"
            size={prominent ? "default" : "sm"}
            className={
              prominent
                ? "gap-2 px-5 py-2.5 text-[15px] font-semibold bg-indigo-600 text-white shadow-md ring-2 ring-indigo-300 ring-offset-1 transition-all duration-200 hover:bg-indigo-500 hover:ring-indigo-400 hover:shadow-lg hover:-translate-y-px my-work-pulse"
                : "gap-2 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
            }
            data-testid="my-work-trigger"
          >
            <UserCheck className={prominent ? "h-5 w-5" : "h-4 w-4"} />
            Il mio lavoro
            {totalCount > 0 && (
              <Badge className={prominent ? "ml-1 bg-white text-indigo-700 hover:bg-white text-xs px-2" : "ml-1 bg-white text-indigo-700 hover:bg-white"}>
                {totalCount}
              </Badge>
            )}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="text-lg flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-indigo-600" />
            Il mio lavoro
            <span className="text-sm font-normal text-slate-500 ml-2">
              {currentOperator?.fullName || "Operatore"}
            </span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Tutte le pratiche, scadenze e note del diario che ti sono state assegnate.
            Filtri Studio/Agenzia restano attivi.
          </p>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-6">

              {/* ====== 1. PRATICHE ====== */}
              <section data-testid="my-work-cases">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                  <Briefcase className="h-4 w-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">
                    Pratiche assegnate
                  </h3>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {myCases.length}
                  </Badge>
                </div>
                {myCases.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-1">
                    Nessuna pratica aperta assegnata a te.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {myCases.slice(0, 20).map((c) => (
                      <li
                        key={c.id}
                        onClick={() => handleNavigate("cases")}
                        className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-blue-50 cursor-pointer group"
                        data-testid={`my-work-case-${c.id}`}
                      >
                        <FolderOpen className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        <span className="font-mono text-xs text-slate-600 shrink-0">{c.caseNumber}</span>
                        <span className="text-slate-800 truncate flex-1">{c.subject || "—"}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{c.status}</Badge>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-blue-500 shrink-0" />
                      </li>
                    ))}
                    {myCases.length > 20 && (
                      <li className="text-xs text-slate-400 text-center pt-1">
                        +{myCases.length - 20} altre…
                      </li>
                    )}
                  </ul>
                )}
              </section>

              {/* ====== 2. LAVORO QUOTIDIANO ====== */}
              <section data-testid="my-work-daily">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                  <ClipboardList className="h-4 w-4 text-teal-700" />
                  <h3 className="text-sm font-semibold text-teal-800 uppercase tracking-wide">
                    Lavoro Quotidiano (in corso)
                  </h3>
                  <Badge variant="outline" className="ml-auto text-xs border-teal-200 text-teal-700">
                    {myDailyPractices.length}
                  </Badge>
                </div>
                {myDailyPractices.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-1">
                    Nessun lavoro in corso registrato a tuo nome.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {myDailyPractices.slice(0, 20).map((p) => (
                      <li
                        key={p.id}
                        onClick={() => handleNavigate("attivita")}
                        className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-teal-50 cursor-pointer group"
                        data-testid={`my-work-daily-${p.id}`}
                      >
                        <span className="h-2 w-2 rounded-full bg-teal-500 shrink-0" />
                        <span className="text-xs text-slate-600 shrink-0 w-20">{fmtDate(p.expectedDueDate)}</span>
                        <span className="text-slate-800 truncate flex-1">
                          {p.clientName || "—"}
                          {p.description ? <span className="text-slate-400"> · {p.description}</span> : null}
                        </span>
                        <Badge variant="outline" className="text-[10px] shrink-0 uppercase">
                          {p.managementType}
                        </Badge>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-teal-500 shrink-0" />
                      </li>
                    ))}
                    {myDailyPractices.length > 20 && (
                      <li className="text-xs text-slate-400 text-center pt-1">
                        +{myDailyPractices.length - 20} altri…
                      </li>
                    )}
                  </ul>
                )}
              </section>

              {/* ====== 3. SCADENZE ====== */}
              <section data-testid="my-work-deadlines">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                  <CalendarClock className="h-4 w-4 text-amber-600" />
                  <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide">
                    Scadenze (prossimi 90 gg)
                  </h3>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {myDeadlines.length}
                  </Badge>
                </div>
                {myDeadlines.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-1">
                    Nessuna scadenza assegnata a te nei prossimi 90 giorni.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {myDeadlines.slice(0, 20).map((d) => (
                      <li
                        key={d.id}
                        onClick={() => handleNavigate("scadenze")}
                        className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-amber-50 cursor-pointer group"
                        data-testid={`my-work-deadline-${d.id}`}
                      >
                        <span className={`h-2 w-2 rounded-full ${urgencyColor(d.deadlineDate)} shrink-0`} />
                        <span className="text-xs text-slate-600 shrink-0 w-20">{fmtDate(d.deadlineDate)}</span>
                        <span className="text-slate-800 truncate flex-1">
                          {d.deadlineType} <span className="text-slate-400">· {d.clientName}</span>
                        </span>
                        <Badge variant="outline" className="text-[10px] shrink-0 uppercase">
                          {d.practiceType}
                        </Badge>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-amber-500 shrink-0" />
                      </li>
                    ))}
                    {myDeadlines.length > 20 && (
                      <li className="text-xs text-slate-400 text-center pt-1">
                        +{myDeadlines.length - 20} altre…
                      </li>
                    )}
                  </ul>
                )}
              </section>

              {/* ====== 4. DIARIO ====== */}
              <section data-testid="my-work-contacts">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                  <MessageSquare className="h-4 w-4 text-emerald-600" />
                  <h3 className="text-sm font-semibold text-emerald-800 uppercase tracking-wide">
                    Diario Giornaliero (azioni richieste)
                  </h3>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {myContacts.length}
                  </Badge>
                </div>
                {myContacts.length === 0 ? (
                  <p className="text-xs text-slate-400 italic px-1">
                    Nessuna nota Diario in attesa di azione da parte tua.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {myContacts.slice(0, 20).map((c) => (
                      <li
                        key={c.id}
                        onClick={() => handleNavigate("attivita")}
                        className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-emerald-50 cursor-pointer group"
                        data-testid={`my-work-contact-${c.id}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-slate-600 shrink-0 w-20">{fmtDate(c.contactDate)}</span>
                        <span className="text-slate-800 truncate flex-1">
                          {c.clientName || "—"} <span className="text-slate-400">· {c.contactType}</span>
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-emerald-500 shrink-0" />
                      </li>
                    ))}
                    {myContacts.length > 20 && (
                      <li className="text-xs text-slate-400 text-center pt-1">
                        +{myContacts.length - 20} altre…
                      </li>
                    )}
                  </ul>
                )}
              </section>

              {totalCount === 0 && (
                <div className="text-center py-6 text-sm text-slate-500">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-400 mb-2" />
                  Tutto sotto controllo! Nessun lavoro assegnato in sospeso.
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default MyWorkPanel;

